import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
interface MenuItem {
  id: string;
  label: string;
  href?: string;
  children?: MenuItem[];
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-16 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative block py-2 font-mono text-[12px] uppercase tracking-[0.1em] text-foreground/80 transition-colors hover:text-foreground">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[1px] w-full origin-center scale-x-0 bg-foreground transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 items-center justify-center">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center gap-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="font-serif text-3xl text-foreground transition-colors hover:text-primary">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return <header id="navigation" className={cn("dark fixed top-0 left-0 z-50 w-full transition-all duration-500 ease-in-out", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/10 shadow-sm" : "bg-background border-b border-transparent")}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="z-50 relative group">
          <span className="font-serif text-2xl font-bold tracking-tight text-foreground">
            FilmFrame
          </span>
        </Link>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="39">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="md:hidden z-50">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-transparent hover:text-primary" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {}
        <div className={cn("fixed inset-0 z-40 bg-background flex flex-col items-center justify-center transition-all duration-500 md:hidden", isMobileMenuOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-4 pointer-events-none")}>
          <MenuProvider menu_id="39">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>
      </div>
    </header>;
}